import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React from "react";
import { useForm } from "react-hook-form";
import { ComplianceFormDocument, ComplianceFormQuestion } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { getDeviceType } from "../../../utils/DeviceUtils";
import { AppButton } from "../../Generic/AppButton";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import { AppTextButton } from "../../Generic/AppTextButton";
import Flex from "../../Generic/Flex";
import { If } from "../../Generic/If";
import OnboardingSectionAppCardHeader from "../../OnboardingSection/OnboardingSectionAppCardHeader";
import ProgressBar from "../../OnboardingSection/ProgressBar";
import Question from "../../OnboardingSection/Question";
import Scaffold from "../../Scaffold/Scaffold";
import ScaffoldTitle from "../../Scaffold/components/ScaffoldTitle";
import ComplianceQuestion from "./ComplianceQuestion";
import { getIsValidAnswer, mapQuestionWithAnswer } from "./complianceFormUtils";
import useComplianceFormStage from "./useComplianceFormStage";

interface Props {
  formDocument: ComplianceFormDocument;
  isSubmitting: boolean;
  onSubmit: (questions: ComplianceFormQuestion[]) => void;
  onClickGoBack: () => void;
  onError: (params: { title: string; message: string }) => void;
}

const ComplianceFormScreen = (props: Props) => {
  const stage = useComplianceFormStage({
    questions: props.formDocument.questions,
    onLastStage: props.onSubmit,
  });

  const { register, setValue, control, handleSubmit } = useForm();

  const handleInvalidSubmit = () => {
    props.onError({
      title: translate(STRINGS.INVALID_FORM),
      message: translate(STRINGS.PLEASE_FILL_REQUIRED_FIELDS),
    });
  };

  const handleValidSubmit = (answers: Record<string, string>) => {
    const questionWithAnswer = mapQuestionWithAnswer(stage.currentQuestion, answers);

    if (!getIsValidAnswer(questionWithAnswer)) {
      handleInvalidSubmit();
      return;
    }

    stage.onRequestNextStage(questionWithAnswer);
  };

  return (
    <Form
      data-fixed-layout={getDeviceType() === "ios-safari"}
      role="form"
      onSubmit={handleSubmit(handleValidSubmit, handleInvalidSubmit)}
    >
      <Scaffold
        title={<ScaffoldTitle label={props.formDocument.documentType.name} />}
        onClickGoBack={props.onClickGoBack}
        footer={
          <ButtonWrapper>
            <AppButton type="submit" isLoading={props.isSubmitting}>
              {translate(STRINGS.NEXT)}
            </AppButton>
          </ButtonWrapper>
        }
      >
        <ContentPadding>
          <Flex direction="column" yGap={16}>
            <AppCard color="primary" variant="soft">
              <AppCardContent>
                <Flex direction="column" yGap={16}>
                  <ProgressBarWrapper>
                    <If condition={stage.currentStageNumber > 1}>
                      <AppTextButton
                        color={"primary"}
                        onClick={stage.onRequestPreviousStage}
                        type="button"
                      >
                        « {translate(STRINGS.PREVIOUS)}
                      </AppTextButton>
                    </If>
                    <ProgressBar percentage={stage.percentage} />
                    <span>
                      <b>{stage.currentStageNumber}</b> {translate(STRINGS.STAGE_X_OF_TOTAL_STAGES)}{" "}
                      {stage.totalStages}
                    </span>
                  </ProgressBarWrapper>
                </Flex>
              </AppCardContent>
            </AppCard>
            <AppCard>
              <OnboardingSectionAppCardHeader title={stage.currentQuestion.title} />
              <If
                condition={
                  stage.currentQuestion.description !== null ||
                  stage.currentQuestion.type !== "plain_text"
                }
              >
                <AppCardContent>
                  {stage.currentQuestion.description === null ? null : (
                    <Question.Group>
                      <Question.Paragraph>{stage.currentQuestion.description}</Question.Paragraph>
                    </Question.Group>
                  )}
                  <Question.Group>
                    {stage.questions.map((question) => {
                      if (question.id === stage.currentQuestion.id) {
                        return (
                          <ComplianceQuestion
                            key={question.id.toString()}
                            question={stage.currentQuestion}
                            control={control}
                            register={register}
                            setValue={setValue}
                            onError={props.onError}
                          />
                        );
                      }
                      return null;
                    })}
                  </Question.Group>
                </AppCardContent>
              </If>
            </AppCard>
          </Flex>
        </ContentPadding>
      </Scaffold>
    </Form>
  );
};

const Form = styled.form`
  height: 100%;
`;

const ContentPadding = styled.div`
  padding: 16px;
`;

const ButtonWrapper = styled.div`
  padding: 16px;
  display: flex;
  .AppButton {
    flex: 1;
  }
`;

const ProgressBarWrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;

  > * {
    font-size: 14px;
    line-height: 100%;
    margin: 0;
    padding: 0;
  }
`;

export default ComplianceFormScreen;
